import { Component, Input, OnInit, EventEmitter, Output, ViewChildren, QueryList, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { IonicModule, ToastController } from '@ionic/angular';
import { FilterInputComponent } from '../filter-input/filter-input.component';
import { CategoriesService } from 'src/app/services/api/categories.service';
import { TranslateConfigService } from 'src/app/services/translate-config.service';
import { AppStorageService } from 'src/app/services/app-storage.service';
import { StorageKeys } from 'src/app/services/storage-keys';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-filters-section',
  templateUrl: './filters-section.component.html',
  styleUrls: ['./filters-section.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, FilterInputComponent]
})
export class FiltersSectionComponent implements OnInit, OnDestroy {
  @Output() filtersApplied = new EventEmitter<any>();

  // Añadir ViewChildren para obtener todas las referencias a los componentes filter-input
  @ViewChildren(FilterInputComponent) filterInputs!: QueryList<FilterInputComponent>;

  // Objeto para almacenar los valores seleccionados
  selectedFilters: any = {};

  // Contador de filtros activos
  activeFiltersCount: number = 0;

  // Propiedades para manejar categorías y subcategorías
  categories: { id: number, name: string }[] = [];
  subcategories: { id: number, name: string }[] = [];
  isLoadingCategories = false;
  isLoadingSubcategories = false;
  categoryError: string | null = null;
  subcategoryError: string | null = null;

  constructor(
    private categoriesService: CategoriesService,
    private translateConfigService: TranslateConfigService,
    private toastController: ToastController,
    private cdr: ChangeDetectorRef,
    private storage: AppStorageService
  ) { }

  @Input() isWeb: boolean = false
  filters: any[] = [
    {
      id: 'category_id',
      label: 'FILTERS.CATEGORY',
      placeholder: 'FILTERS.SELECT',
      type: 'select',
      options: [] // Se llenará dinámicamente
    },
    {
      id: 'subcategory_id',
      label: 'FILTERS.SUBCATEGORY',
      placeholder: 'FILTERS.SELECT_CATEGORY_FIRST',
      type: 'select',
      options: [], // Se llenará dinámicamente
      disabled: true // Inicialmente deshabilitado
    },
    
    {
      id: 'city',
      label: 'FILTERS.CITY',
      placeholder: 'FILTERS.CITY_PLACEHOLDER',
      type: 'autocomplete',
      options: null
    },
    {
      id: 'location',
      label: 'FILTERS.LOCATION',
      placeholder: 'FILTERS.VIEW_MAP',
      type: 'map',
      options: null
    },
    {
      id: 'startDate',
      label: 'FILTERS.START_DATE',
      placeholder: 'FILTERS.SELECT',
      type: 'date',
      options: null
    },
    {
      id: 'endDate',
      label: 'FILTERS.END_DATE',
      placeholder: 'FILTERS.SELECT',
      type: 'date',
      options: null
    }
  ];

  ngOnInit() {
    // Intentar cargar filtros guardados previamente
    this.loadSavedFilters();

    // Cargar categorías activas
    this.loadActiveCategories();

    // Configurar listeners para cambios de categoría
    this.setupCategoryChangeListener();

    // Recargar los listados dependientes del idioma
    this.langSub = this.translateConfigService.onLanguageChange().subscribe(() => {
      this.loadActiveCategories();
      if (this.selectedFilters.category_id) {
        this.loadSubcategoriesByCategory(this.selectedFilters.category_id);
      }
    });
  }

  private langSub?: Subscription;

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  /**
   * Carga las categorías activas desde el servicio
   */
  loadActiveCategories() {
    this.isLoadingCategories = true;
    this.categoryError = null;

    this.categoriesService.getActiveCategoriesForSelect().subscribe({
      next: (categories) => {
        this.categories = categories;
        this.isLoadingCategories = false;

        // Actualizar las opciones de categoría en el formulario
        this.updateCategoryOptions();

        // Si hay una categoría guardada previamente, cargar sus subcategorías
        if (this.selectedFilters.category_id) {
          this.loadSubcategoriesByCategory(this.selectedFilters.category_id);
        }
      },
      error: (error) => {
        this.isLoadingCategories = false;
        this.categoryError = this.translateConfigService.getItemTranslated('CREATE_POST.ERROR_LOADING_CATEGORIES');
      }
    });
  }

  /**
   * Carga las subcategorías activas por ID de categoría
   * @param categoryId ID de la categoría seleccionada
   */
  loadSubcategoriesByCategory(categoryId: number) {
    // Si no hay categoría seleccionada, reiniciar subcategorías
    if (!categoryId) {
      this.subcategories = [];
      this.updateSubcategoryOptions();

      // Limpiar subcategoría seleccionada si existe
      if (this.selectedFilters.subcategory) {
        delete this.selectedFilters.subcategory;
        this.countActiveFilters();
      }

      return;
    }

    // Mostrar spinner de carga
    this.isLoadingSubcategories = true;
    this.subcategoryError = null;

    this.categoriesService.getActiveSubcategoriesForSelect(categoryId).subscribe({
      next: (subcategories) => {
        this.subcategories = subcategories;
        this.isLoadingSubcategories = false;

        // Actualizar las opciones de subcategoría en el formulario
        this.updateSubcategoryOptions();

        // Si no hay subcategorías disponibles, limpiar la selección
        if (subcategories.length === 0) {
          if (this.selectedFilters.subcategory) {
            delete this.selectedFilters.subcategory;
            this.countActiveFilters();
          }
        }
      },
      error: (error) => {
        this.isLoadingSubcategories = false;
        this.subcategoryError = this.translateConfigService.getItemTranslated('CREATE_POST.ERROR_LOADING_SUBCATEGORIES');

        // Limpiar subcategorías en caso de error
        this.subcategories = [];
        this.updateSubcategoryOptions();

        if (this.selectedFilters.subcategory) {
          delete this.selectedFilters.subcategory;
          this.countActiveFilters();
        }
      }
    });
  }

  /**
   * Actualiza las opciones del select de categorías en el filtro
   */
  updateCategoryOptions() {
  const categoryFilter = this.filters.find(filter => filter.id === 'category_id');
    if (categoryFilter) {
      // Añadir opción "Todos" al inicio
      categoryFilter.options = [
        { label: 'FILTERS.ALL', value: '' },
        ...this.categories.map((category: any) => ({
          label: category.name,
          value: category.id
        }))
      ];
    }
  }

  /**
   * Actualiza las opciones del select de subcategorías en el filtro
   */
  updateSubcategoryOptions() {
  const subcategoryFilter = this.filters.find(filter => filter.id === 'subcategory_id');
    if (subcategoryFilter) {
      if (this.subcategories.length > 0) {
        // Añadir opción "Todos" al inicio si hay subcategorías
        subcategoryFilter.options = [
          { label: 'FILTERS.ALL', value: '' },
          ...this.subcategories.map((subcategory: any) => ({
            label: subcategory.name,
            value: subcategory.id
          }))
        ];
        subcategoryFilter.placeholder = 'FILTERS.SELECT';
        subcategoryFilter.disabled = false; // Habilitar cuando hay subcategorías
      } else {
        // Si no hay subcategorías disponibles
        subcategoryFilter.options = [];
  subcategoryFilter.placeholder = this.selectedFilters.category_id ?
          'FILTERS.NO_SUBCATEGORIES' :
          'FILTERS.SELECT_CATEGORY_FIRST';
  subcategoryFilter.disabled = !this.selectedFilters.category_id; // Deshabilitar si no hay categoría
      }
  // Forzar nueva referencia del array para disparar change detection en hijos web
  this.filters = [...this.filters];
  // Detectar cambios manualmente (especialmente para app-select-web)
  this.cdr.detectChanges();
    }
  }

  /**
   * Configura el listener para cambios en la categoría
   */
  setupCategoryChangeListener() {
    // Este método se ejecutará cuando se actualice un filtro
    // La lógica se manejará en updateFilter
  }

  // Cargar filtros guardados
  loadSavedFilters() {
    const savedFilters = this.storage.get(StorageKeys.ACTIVE_FILTERS);
    if (savedFilters) {
      this.selectedFilters = JSON.parse(savedFilters);
      this.countActiveFilters();
    }
  }

  // Método para actualizar un filtro cuando cambia
  updateFilter(event: { id: string, value: any }) {
    this.selectedFilters[event.id] = event.value;

    // Mantener claves normalizadas category_id / subcategory_id
    if (event.id === 'category_id') {
      if (event.value) this.selectedFilters['category_id'] = event.value; else delete this.selectedFilters['category_id'];
    }
    if (event.id === 'subcategory_id') {
      if (event.value) this.selectedFilters['subcategory_id'] = event.value; else delete this.selectedFilters['subcategory_id'];
    }

    // Si se cambió la categoría, recargar subcategorías
  if (event.id === 'category_id') {
      // Limpiar subcategoría seleccionada cuando cambia la categoría
      if (this.selectedFilters.subcategory_id) {
        delete this.selectedFilters.subcategory_id;

        // Resetear el componente de subcategoría
        const subcategoryInput = this.filterInputs.find(input =>
          input.input.id === 'subcategory_id'
        );
        if (subcategoryInput) {
          subcategoryInput.reset();
        }
      }

      // Cargar nuevas subcategorías
      const categoryId = event.value ? Number(event.value) : null;
      if (categoryId) {
        this.loadSubcategoriesByCategory(categoryId);
      } else {
        // Si se selecciona "Todos", limpiar subcategorías
        this.subcategories = [];
        this.updateSubcategoryOptions();
  delete this.selectedFilters.subcategory_id;
  delete this.selectedFilters.subcategory;
      }
    }

    this.countActiveFilters();
  }

  // Contar filtros activos
  countActiveFilters() {
    this.activeFiltersCount = Object.keys(this.selectedFilters).filter(key => {
      const value = this.selectedFilters[key];
  // Evitar contar duplicado: ignorar category_id/subcategory_id (contamos sólo category/subcategory)
  if (key === 'category_id' || key === 'subcategory_id') return false;
      if (key === 'city') {
        return value && value.selected; // solo cuenta si se seleccionó una sugerencia válida
      }
      return value !== null && value !== undefined && value !== '';
    }).length;
  }

  // Aplicar los filtros seleccionados
  applyFilters() {
    // Validación específica: ciudad con texto pero sin selección
    if (this.selectedFilters.city && this.selectedFilters.city.input && !this.selectedFilters.city.selected) {
      this.showCitySelectionToast();
      return; // No aplicar
    }
    // --- Persistir selección de categoría / subcategoría en localStorage para que ListingPage pueda usarlas ---
  try {
      // Limpiar previamente para evitar estados inconsistentes
      this.storage.remove(StorageKeys.SELECTED_CATEGORY);
      this.storage.remove(StorageKeys.SELECTED_SUBCATEGORY);
      this.storage.remove(StorageKeys.IS_SUBCATEGORY);

  const categoryId = this.selectedFilters.category_id;
  const subcategoryId = this.selectedFilters.subcategory_id;

    if (categoryId) {
        const categoryObj = this.categories.find(c => String(c.id) === String(categoryId));
        if (categoryObj) {
          this.storage.setJSON(StorageKeys.SELECTED_CATEGORY, categoryObj);
          this.selectedFilters.category_id = categoryObj.id;
        }

        if (subcategoryId) {
          const subcategoryObj = this.subcategories.find(s => String(s.id) === String(subcategoryId));
          if (subcategoryObj) {
            this.storage.setJSON(StorageKeys.SELECTED_SUBCATEGORY, subcategoryObj);
            this.storage.set(StorageKeys.IS_SUBCATEGORY, 'true');
            this.selectedFilters.subcategory_id = subcategoryObj.id;
          } else {
            delete this.selectedFilters.subcategory_id;
            this.storage.set(StorageKeys.IS_SUBCATEGORY, 'false');
          }
        } else {
          // Categoría sin subcategoría específica
          this.storage.set(StorageKeys.IS_SUBCATEGORY, 'false');
          delete this.selectedFilters.subcategory_id;
        }
      } else {
        // Caso "Todos" => marcar isSubcategory false explícitamente
        this.storage.set(StorageKeys.IS_SUBCATEGORY, 'false');
  // limpiar ids
        delete this.selectedFilters.category_id;
        delete this.selectedFilters.subcategory_id;
      }
    } catch (e) {
    }

    // --- Merge con filtros existentes para no perder p.ej. articleType seteado desde botones rápidos ---
    try {
      const existingRaw = this.storage.get(StorageKeys.ACTIVE_FILTERS);
      const existing = existingRaw ? JSON.parse(existingRaw) : {};
      // Si el usuario ha elegido 'Todos' (category vacía) eliminar previamente restos antiguos
  const isAllCategory = !this.selectedFilters.category_id;
      if (isAllCategory) {
        delete existing.category_id;
        delete existing.subcategory_id;
      }
      // Si ha cambiado de categoría eliminar subcategory previa incoherente
  if (this.selectedFilters.category_id && existing.category_id && String(existing.category_id) !== String(this.selectedFilters.category_id)) {
        delete existing.subcategory;
        delete existing.subcategory_id;
      }

      const merged = { ...existing, ...this.selectedFilters };

      // Si se eliminó la categoría (Todos) limpiar también subcategoría en merged
      if (!merged.category_id) {
        delete merged.subcategory_id;
      }
      this.selectedFilters = merged;
      this.countActiveFilters();
      this.storage.setJSON(StorageKeys.ACTIVE_FILTERS, this.selectedFilters);
    } catch (e) {
      this.storage.setJSON(StorageKeys.ACTIVE_FILTERS, this.selectedFilters);
    }

    this.filtersApplied.emit({
      filters: this.selectedFilters,
      count: this.activeFiltersCount
    });

    // Emitir un evento personalizado para que listening page lo capture
    const event = new CustomEvent('filtersChanged', {
      detail: {
        filters: this.selectedFilters,
        count: this.activeFiltersCount
      },
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  private async showCitySelectionToast() {
    const msg = this.translateConfigService.getItemTranslated('FILTERS.CITY_SELECT_SUGGESTION') || 'Seleccione una ciudad de las sugerencias';
    const toast = await this.toastController.create({
      message: msg,
      duration: 2500,
      color: 'warning',
      position: 'bottom'
    });
    await toast.present();
  }

  // Limpiar todos los filtros
  clearFilters() {
    this.selectedFilters = {};
    this.storage.remove(StorageKeys.ACTIVE_FILTERS);
  this.storage.remove(StorageKeys.SELECTED_CATEGORY);
  this.storage.remove(StorageKeys.SELECTED_SUBCATEGORY);
  this.storage.set(StorageKeys.IS_SUBCATEGORY, 'false');
    this.activeFiltersCount = 0;

    // Restablecer manualmente cada componente filter-input
    if (this.filterInputs) {
      this.filterInputs.forEach(input => {
        // Usar el método público reset
        input.reset();
      });
    }

    // Resetear subcategorías cuando se limpian los filtros
    this.subcategories = [];
    this.updateSubcategoryOptions();

    // Emitir evento de cambio de filtros con valores vacíos
    const event = new CustomEvent('filtersChanged', {
      detail: {
        filters: {},
        count: 0
      },
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  // Obtener valor actual de un filtro
  getFilterValue(filterId: string): any {
    return this.selectedFilters[filterId] || null;
  }

}
